<template>
    <component :is="template">
        <v-container>
            <div class="contents-area">
                <div class="contents-area__inner">
                    <div class="tit-wrap mb-0">
                        <h2 class="tit">상품권 구매하기</h2>
                        <div class="font-size-14 font-size-md-16 grey--text text--garken-3">
                            PG 결제를 통해 나누리 캐시 교환권을 구매하실 수 있습니다.
                        </div>
                    </div>

                    <v-divider class="my-6 my-lg-12" />
                    <purchase-method v-model="form.payment.method" @input="emit" />

                    <v-divider class="my-6 my-lg-12" />
                    <purchase-amount v-model="form" @input="emit" />

                    <template v-if="!user?.code">
                        <v-divider class="my-6 my-lg-12" />
                        <purchase-code v-model="code" />
                    </template>

                    <v-divider class="my-6 my-lg-12" />

                    <purchase-action v-model="form" v-bind="{ loading }" v-on="{ exit, post }" />
                </div>
            </div>
        </v-container>
        <common-loader v-bind="{ loading }" />
        <kcp-cert ref="kcpCert" />
        <checkpay ref="checkpay" @complete="onCheckpayComplete" @close="loading = false" />
    </component>
</template>

<script>
import api from "@/api";
import { mapActions, mapState } from "vuex";
import { initNshopOrder, NshopOrderAider, NSHOP_ORDER_STATES } from "@/store/nshop";

import CommonLoader from "@/components/common/common-loader.vue";
import KcpCert from "@/components/plugins/kcp/kcp-cert.vue";
import Checkpay from "@/components/plugins/checkpay/checkpay.vue";
import PurchaseAmount from "@/components/client/purchase/purchase-amount.vue";
import PurchaseCode from "@/components/client/purchase/purchase-code.vue";
import PurchaseAction from "@/components/client/purchase/purchase-action.vue";
import PurchaseMethod from "@/components/client/purchase/purchase-method.vue";

const defaultCount = 1;
const defaultPriceUnit = 50000;

export default {
    components: {
        CommonLoader,
        KcpCert,
        Checkpay,
        PurchaseAmount,
        PurchaseCode,
        PurchaseAction,
        PurchaseMethod,
    },
    data: () => ({
        form: (() => {
            let form = initNshopOrder({
                count: defaultCount,
                price: {
                    unit: defaultPriceUnit,
                },
            });

            form.price = NshopOrderAider.calcPrice(form);
            return form;
        })(),

        code: null,

        loading: false,
    }),
    computed: {
        ...mapState(["user"]),
        ...mapState("vaccount", { vaccount: "value" }),
        template() {
            return () => import(`@/templates/${this.$theme.pages.gshop}`);
        },
    },
    methods: {
        ...mapActions(["getUser"]),
        ...mapActions("vaccount", { getVaccount: "get", postVaccount: "post" }),

        init() {
            this.loading = false;
        },
        emit() {
            this.form = initNshopOrder(this.form);
        },
        exit() {
            this.$router.go(-1);
        },

        async validate() {
            if (!this.form.payment.method) throw new Error("결제수단을 선택하세요");

            if (!this.$store.state.payload?._user) {
                throw new Error("로그인 후 사용하실 수 있는 기능입니다.");
            }

            if (!this.user) await this.getUser();
            if (!this.user?.code) {
                if (!this.code) throw new Error("추천인 코드를 입력하세요");
                await api.v1.me.profile.put({ _id: this.user?._id, userCode: this.code });
            }

            switch (this.form.payment.method) {
                case "vaccount": {
                    // 진행중인 가상계좌 주문 경고 (바인딩 시 기존 ORDER_INIT 건이 취소된다)
                    let { summary } = await api.v1.me.nshop.orders.gets({
                        headers: { limit: 1 },
                        params: {
                            state: NSHOP_ORDER_STATES.ORDER_INIT.value,
                            ["payment.method"]: this.form.payment.method,
                        },
                    });
                    if (summary.totalCount !== 0) {
                        let go = confirm("이미 진행중인 가상계좌 결제 주문이 있습니다.\n주문을 계속하시면 기존 주문이 취소됩니다.\n\n계속하시겠습니까?");
                        if (!go) throw new Error("취소하셨습니다");
                    }
                    break;
                }
            }
        },
        async post() {
            if (this.loading) return;
            else this.loading = true;

            try {
                await this.validate();
            } catch (error) {
                alert(error.message);
                this.loading = false;
                return;
            }

            try {
                let { ...form } = this.form;

                // 1) 주문 생성 (ORDER_PREP — 결제수단 바인딩 전)
                let { order } = await api.v1.nshop.orders.post(form);

                // 2) 결제수단별 결제 시작 — 주문 생성 → 결제계좌 생성 → 주문·결제 바인딩
                switch (this.form.payment.method) {
                    case "vaccount": {
                        // 가상계좌 생성(없으면) → 주문에 바인딩 (ORDER_PREP → ORDER_INIT)
                        await this.fetchVaccount();
                        await api.v1.nshop.orders.init({ _id: order._id });
                        break;
                    }
                    case "openbank": {
                        // 오픈뱅킹: 본인인증(CI) 확보 → 쿠콘 결제창(iframe). 결과는 onCheckpayComplete 가 처리한다.
                        // (여기서 /purchase/done 으로 보내지 않는다 — 결제 완료 시 complete 핸들러가 이동)
                        if (!this.user?.ci) await this.certify();
                        if (!this.user?.ci) throw new Error("본인인증이 필요합니다");

                        await this.$refs.checkpay.open({ orderNo: order.orderNo, ci: this.user.ci });
                        return;
                    }
                }

                this.$router.push({
                    path: "/purchase/done",
                    query: { _order: order._id },
                });
            } catch (error) {
                alert(error.message);
            } finally {
                this.loading = false;
            }
        },

        /**
         * 오픈뱅킹 결제창 결과 처리. 결과를 알리고 성공이면 결과 페이지로 이동, 실패면 머문다(주문은 ORDER_PREP 로 재시도 가능).
         */
        onCheckpayComplete(query = {}) {
            this.loading = false;

            let { resCode, resMessage, _order } = query;

            if (resCode === "0000") {
                alert(resMessage || "결제가 완료되었습니다.");
                this.$router.push({ path: "/purchase/done", query: { _order } });
            } else {
                alert(resMessage || "결제가 정상적으로 완료되지 않았습니다.");
            }
        },

        /**
         * 본인인증(KCP) 후 회원정보(생년월일·CI)를 갱신한다.
         * @returns {Promise<void>}
         */
        async certify() {
            await new Promise((resolve, reject) => {
                this.$refs.kcpCert.$off();
                this.$refs.kcpCert.$on("input", async ({ _cert }) => {
                    try {
                        await api.v1.me.certificate({ _cert });
                        await this.getUser();
                        resolve();
                    } catch (error) {
                        reject(error);
                    }
                });
                this.$refs.kcpCert.open();
            });
        },

        /** @returns {Promise<import('@/store/user-vaccounts').UserVaccount>} */
        async fetchVaccount() {
            await this.getVaccount();

            if (this.vaccount?._id) return;

            if (!this.user?.birthday) await this.certify();

            await this.postVaccount();
        },

        getErrorMessage({ response, message: baseMessage = "오류가 발생했습니다" }) {
            let { resultCd, resultMsg, advanceMsg } = response?.body?.result || {};

            let messages = [];

            let tag = (() => {
                let tag = [];
                if (resultCd) tag.push(resultCd);
                if (resultMsg) tag.push(resultMsg);

                if (0 < tag.length) return `[${tag.join(":")}]`;
            })();
            if (tag) messages.push(tag);
            if (advanceMsg) messages.push(tag);

            let message = messages.join("\n");
            if (!message) message = baseMessage;

            return message;
        },
    },
    mounted() {
        this.init();
    },
};
</script>

<style lang="scss" scoped>
.contents-area {
    margin: -30px -24px;
    @media (min-width: 768px) {
        margin: -40px -24px;
    }
    @media (min-width: 1024px) {
        margin: 0;
        padding: 60px 114px;
    }
    &__inner {
        @media (max-width: 1023px) {
            padding: 20px 38px;
        }
    }
}
</style>
